import {
  CreditCardIcon,
  LandmarkIcon,
  type LucideIcon,
  PiggyBankIcon,
} from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'

import { PaperSheet } from './PaperSheet'
import { SceneBadge } from './SceneBadge'

const LOOP_SECONDS = 5
const DROP_STAGGER_SECONDS = 0.3
const DROP_HEIGHT = -180

interface DroppedStatement {
  label: string
  icon: LucideIcon
  restX: number
  restRotate: number
}

const STATEMENTS: DroppedStatement[] = [
  { label: 'Checking', icon: LandmarkIcon, restX: -64, restRotate: -9 },
  { label: 'Credit card', icon: CreditCardIcon, restX: 0, restRotate: 2 },
  { label: 'Savings', icon: PiggyBankIcon, restX: 64, restRotate: 10 },
]

// Statements drop one by one into the case folder, which then shows a check.
export const GatherStatementsScene = () => {
  const reduceMotion = useReducedMotion() ?? false
  return (
    <div className="relative mx-auto h-full w-72">
      <span className="absolute inset-x-0 bottom-6 h-28 rounded-xl bg-brand/20">
        <span className="absolute -top-3 left-5 h-4 w-20 rounded-t-lg bg-brand/20" />
      </span>
      {STATEMENTS.map((statement, order) => (
        <FallingStatement
          key={statement.label}
          statement={statement}
          order={order}
          reduceMotion={reduceMotion}
        />
      ))}
      <span className="absolute inset-x-0 bottom-6 h-16 rounded-xl bg-linear-to-b from-brand to-brand-deep px-4 pt-3 text-xs font-semibold text-white shadow-lg">
        Case statements
      </span>
      <FolderBadge reduceMotion={reduceMotion} />
    </div>
  )
}

interface FallingStatementProps {
  statement: DroppedStatement
  order: number
  reduceMotion: boolean
}

const FallingStatement = ({
  statement,
  order,
  reduceMotion,
}: FallingStatementProps) => {
  const { restX, restRotate } = statement
  const rest = { x: restX, y: 0, opacity: 1, rotate: restRotate }
  return (
    <motion.div
      className="absolute bottom-10 left-1/2 -ml-12"
      initial={reduceMotion ? rest : { ...rest, y: DROP_HEIGHT, opacity: 0 }}
      animate={
        reduceMotion
          ? rest
          : {
              y: [DROP_HEIGHT, 0, 0, 0],
              opacity: [0, 1, 1, 0],
              rotate: [restRotate * 3, restRotate, restRotate, restRotate],
            }
      }
      transition={{
        duration: LOOP_SECONDS,
        times: [0, 0.16, 0.86, 1],
        ease: ['backOut', 'linear', 'easeIn'],
        delay: order * DROP_STAGGER_SECONDS,
        repeat: Number.POSITIVE_INFINITY,
      }}
    >
      <PaperSheet label={statement.label} icon={statement.icon} />
    </motion.div>
  )
}

const FolderBadge = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <motion.span
    className="absolute right-3 bottom-[76px]"
    initial={{ scale: reduceMotion ? 1 : 0 }}
    animate={reduceMotion ? { scale: 1 } : { scale: [0, 0, 1, 1, 0] }}
    transition={{
      duration: LOOP_SECONDS,
      times: [0, 0.3, 0.36, 0.86, 0.94],
      ease: 'backOut',
      repeat: Number.POSITIVE_INFINITY,
    }}
  >
    <SceneBadge kind="accepted" />
  </motion.span>
)
